import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';

type SocialSource = 'FACEBOOK' | 'INSTAGRAM';

interface FbComment {
  id: number;
  commentId: string;
  postId: string;
  parentId: string | null;
  parentMessage?: string | null;
  parentFromName?: string | null;
  parentReply?: string | null;
  message: string;
  fromId: string;
  fromName: string;
  reply: string | null;
  csrReplyText: string | null;
  csrReplyBy: string | null;
  csrRepliedAt: string | null;
  csrDeliveryStatus: string | null;
  csrDeliveredAt: string | null;
  csrLastError: string | null;
  omnichannelStatus: string | null;
  assignedAgentUsername: string | null;
  sentimentLabel: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' | 'UNKNOWN' | null;
  sentimentScore: number | null;
  sentimentModel: string | null;
  sentimentRaw: string | null;
  intentLabel: string | null;
  intentScore: number | null;
  intentModel: string | null;
  intentRaw: string | null;
  trendKey: string | null;
  trendTopic: string | null;
  trendScore: number | null;
  status: 'NEW' | 'PROCESSING' | 'REPLIED' | 'HANDED_OFF' | 'FAILED' | 'DRY_RUN_COMPLETED';
  createdAt: string;
  updatedAt: string | null;
  processedAt: string | null;
  retryCount: number;
}

interface FbStats {
  total: number;
  newCount: number;
  processing: number;
  replied: number;
  failed: number;
  handedOff: number;
  positive: number;
  neutral: number;
  negative: number;
  unknownSentiment: number;
  complaints: number;
  statusInquiries: number;
  escalations: number;
  abuse: number;
  topTrends: FbTrend[];
}

interface FbPost {
  id: number;
  postId: string;
  postPreview: string;
  postType: string | null;
  statusType: string | null;
  permalinkUrl: string | null;
  publishedAt: string | null;
  facebookUpdatedAt: string | null;
  lastSyncedAt: string | null;
  commentCount: number;
  negativeCount: number;
  handoffCount: number;
  complaintCount: number;
}

interface FbTrend {
  trendKey: string;
  trendTopic: string;
  count: number;
  negative: number;
  handoff: number;
}

@Component({
  selector: 'app-facebook-comments',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './facebook-comments.component.html',
  styleUrls: ['./facebook-comments.component.scss'],
})
export class FacebookCommentsComponent implements OnInit, OnDestroy {
  source: SocialSource = 'FACEBOOK';
  platformName = 'Facebook';
  platformNoun = 'posts';
  platformItemName = 'post';
  platformIcon = 'thumb_up';
  platformIconClass = 'fb-icon';

  comments: FbComment[] = [];
  posts: FbPost[] = [];
  postsLoading = true;
  stats: FbStats = {
    total: 0, newCount: 0, processing: 0, replied: 0, failed: 0, handedOff: 0,
    positive: 0, neutral: 0, negative: 0, unknownSentiment: 0,
    complaints: 0, statusInquiries: 0, escalations: 0, abuse: 0, topTrends: [],
  };

  totalElements = 0;
  totalPages = 0;
  currentPage = 0;
  readonly pageSize = 20;

  filterStatus = '';
  filterFrom   = '';
  filterTo     = '';
  filterQ      = '';
  filterSentiment = '';
  filterIntent = '';
  filterTrend = '';
  filterPostId = '';

  loading      = true;
  statsLoading = true;

  expandedId: number | null = null;

  private refreshInterval: any = null;

  constructor(private route: ActivatedRoute) {}

  get pageStart(): number { return this.totalElements === 0 ? 0 : this.currentPage * this.pageSize + 1; }
  get pageEnd():   number { return Math.min((this.currentPage + 1) * this.pageSize, this.totalElements); }
  get displayPage(): number { return this.currentPage + 1; }
  get apiSlug(): string { return this.source === 'INSTAGRAM' ? 'instagram' : 'facebook'; }

  ngOnInit(): void {
    const routeSource = String(this.route.snapshot.data['source'] || 'FACEBOOK').toUpperCase();
    this.source = routeSource === 'INSTAGRAM' ? 'INSTAGRAM' : 'FACEBOOK';
    this.applyPlatformLabels();
    void this.loadPosts();
    void this.loadComments();
    this.refreshInterval = setInterval(() => {
      void this.loadComments();
    }, 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  }

  applyFilters(): void {
    this.currentPage = 0;
    void this.loadComments();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterFrom   = '';
    this.filterTo     = '';
    this.filterQ      = '';
    this.filterSentiment = '';
    this.filterIntent = '';
    this.filterTrend = '';
    this.filterPostId = '';
    this.currentPage  = 0;
    void this.loadComments();
  }

  goToPrev(): void {
    if (this.currentPage > 0) { this.currentPage--; void this.loadComments(); }
  }

  goToNext(): void {
    if (this.currentPage < this.totalPages - 1) { this.currentPage++; void this.loadComments(); }
  }

  toggleExpand(id: number): void {
    this.expandedId = this.expandedId === id ? null : id;
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'NEW':        return 'New';
      case 'PROCESSING': return 'Processing';
      case 'REPLIED':    return 'Replied';
      case 'HANDED_OFF': return 'CSR handoff';
      case 'FAILED':     return 'Failed';
      case 'DRY_RUN_COMPLETED': return 'Dry run';
      default:           return status;
    }
  }

  deliveryLabel(status: string | null, comment?: FbComment | null): string {
    if (status === 'PENDING' && !comment?.csrReplyText) return 'Waiting for reply';
    switch (status) {
      case 'PENDING': return 'Delivery pending';
      case 'SENT': return `Sent to ${this.platformName}`;
      case 'DELIVERED': return 'Delivered';
      case 'FAILED': return 'Delivery failed';
      default: return status || '-';
    }
  }

  sentimentLabel(sentiment: string | null): string {
    switch (sentiment) {
      case 'POSITIVE': return 'Positive';
      case 'NEUTRAL': return 'Neutral';
      case 'NEGATIVE': return 'Negative';
      case 'UNKNOWN': return 'Unknown';
      default: return 'Not checked';
    }
  }

  sentimentScore(score: number | null): string {
    return typeof score === 'number' ? `${Math.round(score * 100)}%` : '-';
  }

  intentLabel(intent: string | null): string {
    switch (intent) {
      case 'COMPLAINT': return 'Complaint';
      case 'STATUS_INQUIRY': return 'Status inquiry';
      case 'GENERAL_QUESTION': return 'Question';
      case 'APPRECIATION': return 'Appreciation';
      case 'ABUSE': return 'Abuse';
      case 'SPAM': return 'Spam';
      case 'ESCALATION_REQUEST': return 'Escalation';
      case 'OFF_TOPIC': return 'Off topic';
      case 'UNKNOWN': return 'Unknown';
      default: return intent || 'Not checked';
    }
  }

  scoreText(score: number | null): string {
    return typeof score === 'number' ? `${Math.round(score * 100)}%` : '-';
  }

  isThreadReply(comment: FbComment): boolean {
    return !!comment.parentId;
  }

  parentComment(comment: FbComment): FbComment | null {
    if (!comment.parentId) return null;
    return this.comments.find(c => c.commentId === comment.parentId) ?? null;
  }

  parentCommenter(comment: FbComment): string {
    return comment.parentFromName || this.parentComment(comment)?.fromName || 'Parent comment';
  }

  parentText(comment: FbComment): string {
    return comment.parentMessage || this.parentComment(comment)?.message || '';
  }

  parentAiReply(comment: FbComment): string {
    return comment.parentReply || this.parentComment(comment)?.reply || '';
  }

  postOptionLabel(post: FbPost): string {
    const date = post.publishedAt ? `${this.shortDate(post.publishedAt)} - ` : '';
    const preview = post.postPreview && !post.postPreview.startsWith('Post ')
      ? post.postPreview
      : this.shortPostId(post.postId);
    return `${date}${preview} (${post.commentCount || 0} comments)`;
  }

  get selectedPost(): FbPost | null {
    return this.posts.find(post => post.postId === this.filterPostId) ?? null;
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) +
           ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  shortDate(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString([], { day: '2-digit', month: 'short' });
  }

  shortPostId(postId: string): string {
    if (!postId) return `${this.platformName} ${this.platformItemName}`;
    const parts = postId.split('_');
    const id = parts.length > 1 ? parts[1] : postId;
    return `${this.platformName} ${this.platformItemName} ${id.slice(-8)}`;
  }

  private authHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth-token') ?? '';
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' }
      : { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  async loadStats(): Promise<void> {
    this.statsLoading = true;
    try {
      const res = await fetch(`${environment.apiHost}/api/v1/${this.apiSlug}/stats`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(this.filterBody(false)),
      });
      if (!res.ok) return;
      const json: any = await res.json();
      this.stats = { ...this.stats, ...(json?.data ?? json) };
    } catch { /* silently ignore */ }
    finally { this.statsLoading = false; }
  }

  async loadPosts(): Promise<void> {
    this.postsLoading = true;
    try {
      const res = await fetch(`${environment.apiHost}/api/v1/${this.apiSlug}/posts`, {
        method: 'GET',
        headers: this.authHeaders(),
      });
      if (!res.ok) return;
      const json: any = await res.json();
      const payload = json?.data ?? json;
      this.posts = payload?.posts ?? [];
    } catch {
      this.posts = [];
    } finally {
      this.postsLoading = false;
    }
  }

  async loadComments(): Promise<void> {
    this.loading = true;
    this.statsLoading = true;
    const body = this.filterBody(true);

    try {
      const res = await fetch(`${environment.apiHost}/api/v1/${this.apiSlug}/comments`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) { this.loading = false; return; }
      const json: any = await res.json();
      const payload = json?.data ?? json;
      this.comments      = payload?.comments      ?? [];
      this.totalElements = payload?.totalElements  ?? 0;
      this.totalPages    = payload?.totalPages     ?? 0;
      if (payload?.stats) {
        this.stats = { ...this.stats, ...payload.stats };
      }
    } catch { this.comments = []; }
    finally {
      this.loading = false;
      this.statsLoading = false;
    }
  }

  private filterBody(includePaging: boolean): any {
    const body: any = includePaging ? { page: this.currentPage, size: this.pageSize } : {};
    if (this.filterStatus) body['status'] = this.filterStatus;
    if (this.filterSentiment) body['sentiment'] = this.filterSentiment;
    if (this.filterIntent) body['intent'] = this.filterIntent;
    if (this.filterTrend) body['trend'] = this.filterTrend;
    if (this.filterPostId) body['postId'] = this.filterPostId;
    if (this.filterFrom) body['from'] = `${this.filterFrom}T00:00:00`;
    if (this.filterTo) body['to'] = `${this.filterTo}T23:59:59`;
    if (this.filterQ.trim()) body['q'] = this.filterQ.trim();
    return body;
  }

  private applyPlatformLabels(): void {
    if (this.source === 'INSTAGRAM') {
      this.platformName = 'Instagram';
      this.platformNoun = 'media';
      this.platformItemName = 'media';
      this.platformIcon = 'photo_camera';
      this.platformIconClass = 'instagram-icon';
      return;
    }
    this.platformName = 'Facebook';
    this.platformNoun = 'posts';
    this.platformItemName = 'post';
    this.platformIcon = 'thumb_up';
    this.platformIconClass = 'fb-icon';
  }
}
